import { useTranslation } from 'react-i18next'
import { ArrowRight } from 'lucide-react'
import { usePageTitle } from '@/hooks/usePageTitle'
import { LocalizedLink } from '@/components/LocalizedLink'
import { CtaBanner, PageHero, ScrollReveal } from '@/components/ui'

const areaKeys = [
  'design',
  'civil',
  'industrial',
  'commercial',
  'renovation',
  'maintenance',
] as const

const areaImages: Record<(typeof areaKeys)[number], string> = {
  design: '/media/images/services/design.jpg',
  civil: '/media/images/services/civil.jpg',
  industrial: '/media/images/services/industrial.jpg',
  commercial: '/media/images/services/commercial.jpg',
  renovation: '/media/images/services/renovation.jpg',
  maintenance: '/media/images/services/maintenance.jpg',
}

export default function Services() {
  const { t } = useTranslation()
  usePageTitle('servicesPage.meta.title', 'servicesPage.meta.description')

  return (
    <main id="main-content">
      {/* Hero grafico — base per tutte le pagine */}
      <PageHero
        title={t('servicesPage.title')}
        subtitle={t('servicesPage.subtitle')}
      />

      {/* Section per ogni servizio: testo + elenco a sinistra, immagine a destra */}
      {areaKeys.map((key, index) => {
        const items = t(`servicesPage.areas.${key}.items`, { returnObjects: true }) as string[]
        const isReversed = index % 2 === 1

        return (
          <section
            key={key}
            className={`py-16 md:py-24 ${index % 2 === 0 ? 'bg-surface' : 'bg-bg'}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <ScrollReveal>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                  <div className={isReversed ? 'lg:order-2' : ''}>
                    <h2 className="font-display text-h2 text-heading mb-4">
                      {t(`servicesPage.areas.${key}.title`)}
                    </h2>
                    <p className="text-text text-lg leading-relaxed mb-6">
                      {t(`servicesPage.areas.${key}.description`)}
                    </p>
                    <ul className="space-y-3">
                      {items.map((item) => (
                        <li key={item} className="flex items-start gap-3 text-text">
                          <span
                            className="mt-1.5 h-2 w-2 rounded-full bg-brand flex-shrink-0"
                            aria-hidden="true"
                          />
                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={isReversed ? 'lg:order-1' : ''}>
                    <img
                      src={areaImages[key]}
                      alt={t(`servicesPage.areas.${key}.title`)}
                      loading="lazy"
                      className="w-full rounded-xl shadow-lg object-cover aspect-[4/3]"
                    />
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </section>
        )
      })}

      {/* CTA */}
      <CtaBanner
        title={t('servicesPage.cta.title')}
        action={
          <LocalizedLink
            to="contacts"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-heading font-semibold rounded-lg hover:bg-bg transition-colors shadow-lg"
          >
            {t('buttons.contactUs')}
            <ArrowRight className="h-5 w-5" />
          </LocalizedLink>
        }
      />
    </main>
  )
}
